'use client';

import { createClient } from '@/lib/supabase/client';

export interface Film {
  id: string;
  title: string;
  originalTitle: string;
  tagline: string;
  synopsis: string;
  shortDescription: string;
  year: number;
  runtimeMinutes: number;
  runtimeDisplay: string;
  filmType: string;
  contentRating: string;
  country: string;
  languages: string[];
  genres: string[];
  themes: string[];
  watchStyles: string[];
  festivals: string[];
  festivalBadge: string;
  directorName: string;
  castCrew: any[];
  posterUrl: string;
  posterAlt: string;
  backdropUrl: string;
  backdropAlt: string;
  muxAssetId: string;
  muxPlaybackId: string;
  muxStatus: string;
  muxTrailerPlaybackId: string;
  pubStatus: string;
  publishedAt: string | null;
  isStaffPick: boolean;
  isNew: boolean;
  isFeatured: boolean;
  viewCount: number;
  completionRate: number;
  createdAt: string;
  filmfreewayUrl: string;
  imdbUrl: string;
  contentFlags: string[];
}

export interface ContinueWatchingItem {
  film: Film;
  resumeSeconds: number;
  progressPercent: number;
}

function mapFilm(row: any): Film {
  return {
    id: row.id,
    title: row.title,
    originalTitle: row.original_title || '',
    tagline: row.tagline || '',
    synopsis: row.synopsis || '',
    shortDescription: row.short_description || '',
    year: row.year,
    runtimeMinutes: row.runtime_minutes || 0,
    runtimeDisplay: row.runtime_display || '',
    filmType: row.film_type,
    contentRating: row.content_rating || 'NR',
    country: row.country || '',
    languages: row.languages || [],
    genres: row.genres || [],
    themes: row.themes || [],
    watchStyles: row.watch_styles || [],
    festivals: row.festivals || [],
    festivalBadge: row.festival_badge || '',
    directorName: row.director_name || '',
    castCrew: row.cast_crew || [],
    posterUrl: row.poster_url || '',
    posterAlt: row.poster_alt || '',
    backdropUrl: row.backdrop_url || '',
    backdropAlt: row.backdrop_alt || '',
    muxAssetId: row.mux_asset_id || '',
    muxPlaybackId: row.mux_playback_id || '',
    muxStatus: row.mux_status || 'none',
    muxTrailerPlaybackId: row.mux_trailer_playback_id || '',
    pubStatus: row.pub_status,
    publishedAt: row.published_at || null,
    isStaffPick: row.is_staff_pick || false,
    isNew: row.is_new || false,
    isFeatured: row.is_featured || false,
    viewCount: row.view_count || 0,
    completionRate: row.completion_rate || 0,
    createdAt: row.created_at,
    filmfreewayUrl: row.filmfreeway_url || '',
    imdbUrl: row.imdb_url || '',
    contentFlags: row.content_flags || [],
  };
}

export const filmService = {
  async getPublished(): Promise<Film[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('films')
      .select('*')
      .eq('pub_status', 'published')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('filmService.getPublished error:', error.message);
      return [];
    }
    return (data || []).map(mapFilm);
  },

  async getAll(): Promise<Film[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('films')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('filmService.getAll error:', error.message);
      return [];
    }
    return (data || []).map(mapFilm);
  },

  async getById(id: string): Promise<Film | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('films')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('filmService.getById error:', error.message);
      return null;
    }
    return data ? mapFilm(data) : null;
  },

  async search(query: string, filters?: { types?: string[]; genres?: string[] }): Promise<Film[]> {
    const supabase = createClient();
    let q = supabase
      .from('films')
      .select('*')
      .eq('pub_status', 'published');

    if (query) {
      q = q.ilike('title', `%${query}%`);
    }
    if (filters?.types && filters.types.length > 0) {
      q = q.in('film_type', filters.types);
    }
    if (filters?.genres && filters.genres.length > 0) {
      q = q.overlaps('genres', filters.genres);
    }

    const { data, error } = await q.order('created_at', { ascending: false });

    if (error) {
      console.error('filmService.search error:', error.message);
      return [];
    }
    return (data || []).map(mapFilm);
  },

  async create(filmData: Partial<Record<string, any>>): Promise<Film | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('films')
      .insert({ ...filmData, created_by: user.id })
      .select()
      .single();

    if (error) {
      console.error('filmService.create error:', error.message);
      throw error;
    }
    return data ? mapFilm(data) : null;
  },

  async update(id: string, updates: Partial<Record<string, any>>): Promise<Film | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('films')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('filmService.update error:', error.message);
      throw error;
    }
    return data ? mapFilm(data) : null;
  },

  async updateMuxPlaybackId(id: string, playbackId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('films')
      .update({
        mux_playback_id: playbackId,
        mux_status: playbackId ? 'ready' : 'none',
      })
      .eq('id', id);

    if (error) {
      console.error('filmService.updateMuxPlaybackId error:', error.message);
      return false;
    }
    return true;
  },

  async publish(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('films')
      .update({ pub_status: 'published', published_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('filmService.publish error:', error.message);
      return false;
    }
    return true;
  },

  async archive(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('films')
      .update({ pub_status: 'archived' })
      .eq('id', id);

    if (error) {
      console.error('filmService.archive error:', error.message);
      return false;
    }
    return true;
  },

  async recordWatchHistory(filmId: string): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return; // silently skip for unauthenticated users

    // Upsert: if a row already exists for this user+film, update last_watched_at
    const { error } = await supabase
      .from('watch_history')
      .upsert(
        {
          user_id: user.id,
          film_id: filmId,
          last_watched_at: new Date().toISOString(),
          progress_percent: 0,
          completed: false,
        },
        { onConflict: 'user_id,film_id', ignoreDuplicates: false }
      );

    if (error) {
      console.error('filmService.recordWatchHistory error:', error.message);
    }
  },

  async getContinueWatching(): Promise<ContinueWatchingItem[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('watch_history')
      .select('film_id, last_watched_at, resume_seconds, progress_percent, films(*)')
      .eq('user_id', user.id)
      .eq('completed', false)
      .order('last_watched_at', { ascending: false })
      .limit(12);

    if (error) {
      console.error('filmService.getContinueWatching error:', error.message);
      return [];
    }

    return (data || [])
      .filter((row: any) => row.films)
      .map((row: any) => ({
        film: mapFilm(row.films),
        resumeSeconds: row.resume_seconds ?? 0,
        progressPercent: row.progress_percent ?? 0,
      }));
  },

  async getResumeTimestamp(filmId: string): Promise<number> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { data, error } = await supabase
      .from('watch_history')
      .select('resume_seconds')
      .eq('user_id', user.id)
      .eq('film_id', filmId)
      .maybeSingle();

    if (error) {
      console.error('filmService.getResumeTimestamp error:', error.message);
      return 0;
    }
    return (data as any)?.resume_seconds ?? 0;
  },

  async saveResumeTimestamp(filmId: string, seconds: number): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('watch_history')
      .upsert(
        {
          user_id: user.id,
          film_id: filmId,
          resume_seconds: Math.floor(seconds),
          last_watched_at: new Date().toISOString(),
          progress_percent: 0,
          completed: false,
        },
        { onConflict: 'user_id,film_id', ignoreDuplicates: false }
      );

    if (error) {
      console.error('filmService.saveResumeTimestamp error:', error.message);
    }
  },

  /**
   * Update watch_history with progress_percent and completed flag.
   * Called after each playback session ends so Continue Watching and
   * personalized recommendations stay accurate.
   *
   * @param filmId - the film being watched
   * @param endTimeSeconds - where the user stopped (seconds)
   * @param durationSeconds - total film duration (seconds)
   */
  async updateWatchProgress(
    filmId: string,
    endTimeSeconds: number,
    durationSeconds: number,
  ): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const progressPercent =
      durationSeconds > 0
        ? Math.min(100, Math.round((endTimeSeconds / durationSeconds) * 100))
        : 0;

    // A film is considered "completed" when the user has watched ≥ 90 %
    const completed = progressPercent >= 90;

    const { error } = await supabase
      .from('watch_history')
      .upsert(
        {
          user_id: user.id,
          film_id: filmId,
          progress_percent: progressPercent,
          completed,
          resume_seconds: completed ? 0 : Math.floor(endTimeSeconds),
          last_watched_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,film_id', ignoreDuplicates: false }
      );

    if (error) {
      console.error('filmService.updateWatchProgress error:', error.message);
    }
  },

  async batchPublish(ids: string[]): Promise<boolean> {
    if (!ids.length) return true;
    const supabase = createClient();
    const { error } = await supabase
      .from('films')
      .update({ pub_status: 'published', published_at: new Date().toISOString() })
      .in('id', ids);
    if (error) {
      console.error('filmService.batchPublish error:', error.message);
      return false;
    }
    return true;
  },

  async batchArchive(ids: string[]): Promise<boolean> {
    if (!ids.length) return true;
    const supabase = createClient();
    const { error } = await supabase
      .from('films')
      .update({ pub_status: 'archived' })
      .in('id', ids);
    if (error) {
      console.error('filmService.batchArchive error:', error.message);
      return false;
    }
    return true;
  },

  async batchReassignGenres(ids: string[], genres: string[]): Promise<boolean> {
    if (!ids.length) return true;
    const supabase = createClient();
    const { error } = await supabase
      .from('films')
      .update({ genres })
      .in('id', ids);
    if (error) {
      console.error('filmService.batchReassignGenres error:', error.message);
      return false;
    }
    return true;
  },

  async getFeatured(): Promise<Film[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('films')
      .select('*')
      .eq('pub_status', 'published')
      .eq('is_featured', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('filmService.getFeatured error:', error.message);
      return [];
    }
    return (data || []).map(mapFilm);
  },

  async getNewReleases(limit = 60): Promise<Film[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('films')
      .select('*')
      .eq('pub_status', 'published')
      .order('published_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('filmService.getNewReleases error:', error.message);
      return [];
    }
    return (data || []).map(mapFilm);
  },

  async toggleFeatured(id: string, featured: boolean): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('films')
      .update({ is_featured: featured })
      .eq('id', id);

    if (error) {
      console.error('filmService.toggleFeatured error:', error.message);
      return false;
    }
    return true;
  },

  /**
   * Build personalized rails from the authenticated user's watch_history.
   * Returns an object with named rails derived from:
   *  - genre_interests: genres of films the user has watched
   *  - completion_rate: films the user completed (progress_percent >= 80)
   *  - watched_ids: films already seen (excluded from "Recommended For You")
   */
  async getPersonalizedRails(): Promise<{
    recommendedForYou: Film[];
    becauseYouWatched: Film[];
    topGenre: { label: string; films: Film[] } | null;
    highlyCompleted: Film[];
    watchedGenres: string[];
    isPersonalized: boolean;
  }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    const empty = {
      recommendedForYou: [] as Film[],
      becauseYouWatched: [] as Film[],
      topGenre: null as null,
      highlyCompleted: [] as Film[],
      watchedGenres: [] as string[],
      isPersonalized: false,
    };

    if (!user) return empty;

    // 1. Fetch user's watch history with film genre data
    const { data: historyRows, error: historyErr } = await supabase
      .from('watch_history')
      .select('film_id, progress_percent, completed, films(id, genres, film_type)')
      .eq('user_id', user.id)
      .order('last_watched_at', { ascending: false });

    if (historyErr || !historyRows || historyRows.length === 0) return empty;

    // 2. Build genre interest map from watch history
    const genreCount: Record<string, number> = {};
    const watchedFilmIds = new Set<string>();
    let completedCount = 0;

    for (const row of historyRows) {
      const film = row.films as any;
      if (!film) continue;
      watchedFilmIds.add(row.film_id);

      // Weight completed films more heavily
      const weight = row.completed || (row.progress_percent ?? 0) >= 80 ? 2 : 1;
      if (row.completed || (row.progress_percent ?? 0) >= 80) completedCount++;

      for (const genre of (film.genres || [])) {
        genreCount[genre] = (genreCount[genre] || 0) + weight;
      }
    }

    // Sort genres by interest score
    const sortedGenres = Object.entries(genreCount)
      .sort((a, b) => b[1] - a[1])
      .map(([g]) => g);

    if (sortedGenres.length === 0) return empty;

    const topGenreName = sortedGenres[0];
    const interestGenres = sortedGenres.slice(0, 5);
    const watchedIdsArray = Array.from(watchedFilmIds);

    // 3. Fetch published films NOT already watched, matching top genres
    const { data: candidateRows, error: candidateErr } = await supabase
      .from('films')
      .select('*')
      .eq('pub_status', 'published')
      .not('id', 'in', `(${watchedIdsArray.join(',')})`)
      .overlaps('genres', interestGenres)
      .order('created_at', { ascending: false })
      .limit(40);

    if (candidateErr) {
      console.error('filmService.getPersonalizedRails candidates error:', candidateErr.message);
      return empty;
    }

    const candidates = (candidateRows || []).map(mapFilm);

    // 4. Score candidates: more matching genres = higher score
    const scored = candidates.map(film => {
      let score = 0;
      for (let i = 0; i < interestGenres.length; i++) {
        if (film.genres.includes(interestGenres[i])) {
          score += (interestGenres.length - i); // higher weight for top genres
        }
      }
      return { film, score };
    }).sort((a, b) => b.score - a.score);

    const recommendedForYou = scored.slice(0, 12).map(s => s.film);

    // 5. "Because You Watched" — films similar to the most recently watched film
    const mostRecentFilmId = historyRows[0]?.film_id;
    let becauseYouWatched: Film[] = [];
    if (mostRecentFilmId) {
      const { data: recentFilm } = await supabase
        .from('films')
        .select('genres, film_type')
        .eq('id', mostRecentFilmId)
        .maybeSingle();

      if (recentFilm && (recentFilm.genres || []).length > 0) {
        const { data: similarRows } = await supabase
          .from('films')
          .select('*')
          .eq('pub_status', 'published')
          .neq('id', mostRecentFilmId)
          .overlaps('genres', recentFilm.genres)
          .order('view_count', { ascending: false })
          .limit(12);

        becauseYouWatched = (similarRows || []).map(mapFilm);
      }
    }

    // 6. Top genre rail — films in the user's #1 genre
    const { data: topGenreRows } = await supabase
      .from('films')
      .select('*')
      .eq('pub_status', 'published')
      .overlaps('genres', [topGenreName])
      .order('view_count', { ascending: false })
      .limit(12);

    const topGenreFilms = (topGenreRows || []).map(mapFilm);

    // 7. Highly completed films — films with high platform completion_rate
    //    that the user hasn't seen yet
    const { data: completedRows } = await supabase
      .from('films')
      .select('*')
      .eq('pub_status', 'published')
      .not('id', 'in', `(${watchedIdsArray.join(',')})`)
      .gte('completion_rate', 70)
      .order('completion_rate', { ascending: false })
      .limit(12);

    const highlyCompleted = (completedRows || []).map(mapFilm);

    return {
      recommendedForYou,
      becauseYouWatched,
      topGenre: topGenreFilms.length > 0 ? { label: topGenreName, films: topGenreFilms } : null,
      highlyCompleted,
      watchedGenres: sortedGenres,
      isPersonalized: true,
    };
  },
};
